import { useEffect, useRef } from 'react';

import {
  normalizeDatadogFeatureFlagKey,
  normalizeDatadogFeatureFlagValue,
} from 'features/observability/rum/datadog-feature-flag-utils';
import { trackDatadogFeatureFlagEvaluation } from 'features/observability/rum/track-datadog-feature-flag-evaluation';

import {
  BooleanFlags,
  FlagType,
  LaunchDarklyFlag,
  NumericFlags,
  StringFlags,
  VariationFlags,
} from './flags';
import { useFlagCore } from './platform-client';
import safeFlagDefaults from './safe-flag-defaults';

export { LaunchDarklyFlag };

export function useFlag<T>(flag: VariationFlags): T | null | undefined;
export function useFlag(flag: NumericFlags): FlagType<NumericFlags>;
export function useFlag(flag: StringFlags): FlagType<StringFlags> | undefined;
export function useFlag(flag: BooleanFlags): FlagType<BooleanFlags>;
export function useFlag(flag: LaunchDarklyFlag): FlagType<LaunchDarklyFlag> {
  const flagValue = useFlagCore(flag, safeFlagDefaults[flag]);
  const lastTrackedValue = useRef<string | undefined>(undefined);

  useEffect(() => {
    const normalizedFlagKey = normalizeDatadogFeatureFlagKey(flag);
    const normalizedValue = normalizeDatadogFeatureFlagValue(flagValue);
    const trackedEvaluationKey = `${normalizedFlagKey}:${normalizedValue.cacheKey}`;

    if (lastTrackedValue.current === trackedEvaluationKey) {
      return;
    }

    lastTrackedValue.current = trackedEvaluationKey;
    trackDatadogFeatureFlagEvaluation(normalizedFlagKey, normalizedValue.value);
  }, [flag, flagValue]);

  return flagValue;
}
